// DailyAttendanceChart.jsx - FIXED TO USE REAL API DATA
import React, { useState, useEffect, useRef } from "react";

// ✅ Accept data as prop instead of hardcoded values
const DailyAttendanceChart = ({ data = [] }) => {
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, data: null });
  const [isMobile, setIsMobile] = useState(false);
  const chartRef = useRef(null);
  const tooltipRef = useRef(null);
  
  // ✅ Process real API data
  const chartData = React.useMemo(() => {
    if (!data || data.length === 0) return { dates: [], registrations: [], checkedIn: [], maxCapacity: 0 };
    
    const dates = data.map(d => {
      // Convert "Day 1" to readable date or use actual date if available
      const dateObj = new Date();
      return d.day || `Day ${dateObj.getDate()}`;
    });
    
    const registrations = data.map(d => d.registered || 0);
    const checkedIn = data.map(d => d.attended || d.checkedIn || 0);
    
    // Calculate max capacity (highest value + 10% buffer)
    const maxValue = Math.max(...registrations, ...checkedIn);
    const maxCapacity = Math.ceil(maxValue * 1.1);
    
    return { dates, registrations, checkedIn, maxCapacity };
  }, [data]);

  // Responsive configuration
  const PADDING = { top: 35, right: 25, bottom: 50, left: 60 };
  const MAX_MOBILE_WIDTH = 768;
  const MIN_HEIGHT = 280;
  const TOOLTIP_OFFSET = 15;
  const MIN_DIST_THRESHOLD = 25;

  // Detect mobile view
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= MAX_MOBILE_WIDTH);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Calculate chart dimensions based on container
  const getChartDimensions = () => {
    if (!chartRef.current) return { 
      width: 600, 
      height: 300,
      innerWidth: 530,
      innerHeight: 220
    };
    
    const width = chartRef.current.clientWidth;
    const height = Math.max(MIN_HEIGHT, width * 0.45);
    const innerWidth = width - PADDING.left - PADDING.right;
    const innerHeight = height - PADDING.top - PADDING.bottom;
    
    return { width, height, innerWidth, innerHeight };
  };

  const { width, height, innerWidth, innerHeight } = getChartDimensions();
  const { dates, registrations, checkedIn, maxCapacity } = chartData;
  
  // Calculate positions for data points
  const datePositions = dates.length > 1 
    ? dates.map((_, i) => PADDING.left + (i * (innerWidth / (dates.length - 1))))
    : [PADDING.left + innerWidth / 2]; // Single data point

  // Calculate Y position based on data value
  const getYPosition = (value) => {
    return PADDING.top + innerHeight - (value / maxCapacity) * innerHeight;
  };

  // Handle mouse interactions
  const handleMouseMove = (e) => {
    if (!chartRef.current || isMobile || dates.length === 0) return;
    
    const rect = chartRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    
    // Find closest point
    let closestIndex = 0;
    let minDist = Infinity;
    
    datePositions.forEach((pos, index) => {
      const dist = Math.abs(mouseX - pos);
      if (dist < minDist && dist < MIN_DIST_THRESHOLD) {
        minDist = dist;
        closestIndex = index;
      }
    });
    
    if (minDist < MIN_DIST_THRESHOLD) {
      const tooltipWidth = tooltipRef.current?.offsetWidth || 160;
      const tooltipHeight = tooltipRef.current?.offsetHeight || 80;
      
      let tooltipX = mouseX + TOOLTIP_OFFSET;
      let tooltipY = mouseY - tooltipHeight - TOOLTIP_OFFSET;
      
      if (tooltipX + tooltipWidth > rect.width - 10) {
        tooltipX = mouseX - tooltipWidth - TOOLTIP_OFFSET;
      }
      if (tooltipY < 10) tooltipY = 10;
      if (tooltipY + tooltipHeight > rect.height - 10) {
        tooltipY = rect.height - tooltipHeight - 10;
      }
      
      setTooltip({
        visible: true,
        x: tooltipX,
        y: tooltipY,
        data: {
          date: dates[closestIndex],
          registrations: registrations[closestIndex],
          checkedIn: checkedIn[closestIndex]
        }
      });
    } else {
      setTooltip(prev => prev.visible ? { ...prev, visible: false } : prev);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) setTooltip({ visible: false });
  };

  // Mobile touch handling
  const handleTouchStart = (e) => {
    if (!chartRef.current || dates.length === 0) return;
    
    const rect = chartRef.current.getBoundingClientRect();
    const touchX = e.touches[0].clientX - rect.left;
    
    let closestIndex = 0;
    let minDist = Infinity;
    
    datePositions.forEach((pos, index) => {
      const dist = Math.abs(touchX - pos);
      if (dist < minDist) {
        minDist = dist;
        closestIndex = index;
      }
    });
    
    if (minDist < 40) {
      const tooltipWidth = 160;
      const tooltipX = Math.min(
        Math.max(datePositions[closestIndex] - tooltipWidth / 2, PADDING.left),
        width - PADDING.right - tooltipWidth
      );
      
      setTooltip({
        visible: true,
        x: tooltipX,
        y: PADDING.top + 20,
        data: {
          date: dates[closestIndex],
          registrations: registrations[closestIndex],
          checkedIn: checkedIn[closestIndex]
        }
      });
    }
  };

  const handleTouchEnd = () => {
    if (isMobile) setTooltip({ visible: false });
  };

  // ✅ Show empty state if no data
  if (!data || data.length === 0) {
    return (
      <div className="daily-attendance-container">
        <div className="chart-header">
          <h2 className="chart-title">Daily Attendance Overview</h2>
          <p className="chart-subtitle">Registration and check-in status across event days</p>
        </div>
        <div className="chart-empty-state">
          <p>No attendance data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="daily-attendance-container">
      <div className="chart-header">
        <h2 className="chart-title">Daily Attendance Overview</h2>
        <p className="chart-subtitle">Registration and check-in status across event days</p>
      </div>
      
      <div 
        ref={chartRef}
        className="chart-wrapper"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        role="img"
        aria-label="Line chart showing daily attendance with registration and check-in data. Tap or hover on data points for details."
      >
        <svg 
          width="100%" 
          height={height} 
          viewBox={`0 0 ${width} ${height}`} 
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="xMidYMid meet"
        >
          {/* Grid lines */}
          <g stroke="#e2e8f0" strokeWidth="1">
            <line x1={PADDING.left} y1={PADDING.top + innerHeight} x2={width - PADDING.right} y2={PADDING.top + innerHeight} />
            <line x1={PADDING.left} y1={PADDING.top + innerHeight * 0.75} x2={width - PADDING.right} y2={PADDING.top + innerHeight * 0.75} />
            <line x1={PADDING.left} y1={PADDING.top + innerHeight * 0.5} x2={width - PADDING.right} y2={PADDING.top + innerHeight * 0.5} />
            <line x1={PADDING.left} y1={PADDING.top + innerHeight * 0.25} x2={width - PADDING.right} y2={PADDING.top + innerHeight * 0.25} />
          </g>
          
          {/* Y-axis labels */}
          <g fontSize="12" fill="#64748b" textAnchor="end" fontFamily="Inter, -apple-system, sans-serif">
            <text x={PADDING.left - 10} y={PADDING.top + innerHeight + 5}>0</text>
            <text x={PADDING.left - 10} y={PADDING.top + innerHeight * 0.75 + 5}>{Math.round(maxCapacity * 0.25)}</text>
            <text x={PADDING.left - 10} y={PADDING.top + innerHeight * 0.5 + 5}>{Math.round(maxCapacity * 0.5)}</text>
            <text x={PADDING.left - 10} y={PADDING.top + innerHeight * 0.25 + 5}>{Math.round(maxCapacity * 0.75)}</text>
            <text x={PADDING.left - 10} y={PADDING.top + 5}>{maxCapacity}</text>
          </g>
          
          {/* X-axis labels */}
          <g fontSize="12" fill="#64748b" textAnchor="middle" fontFamily="Inter, -apple-system, sans-serif">
            {dates.map((date, i) => (
              <text 
                key={`x-${i}`} 
                x={datePositions[i]} 
                y={height - PADDING.bottom + 15}
                className="x-axis-label"
              >
                {date}
              </text>
            ))}
          </g>
          
          {/* Capacity line */}
          <line 
            x1={PADDING.left} 
            y1={PADDING.top} 
            x2={width - PADDING.right} 
            y2={PADDING.top} 
            stroke="#94a3b8" 
            strokeWidth="1"
            strokeDasharray="4,4"
          />
          
          {/* Registrations Area (Orange) */}
          {registrations.length > 0 && (
            <path 
              d={`M${PADDING.left},${getYPosition(registrations[0])} 
                  ${datePositions.map((pos, i) => `L${pos},${getYPosition(registrations[i])}`).join(' ')}
                  L${width - PADDING.right},${getYPosition(registrations[registrations.length - 1])}
                  L${width - PADDING.right},${PADDING.top + innerHeight}
                  L${PADDING.left},${PADDING.top + innerHeight} Z`}
              fill="url(#regGradient)"
              opacity="0.2"
            />
          )}
          
          {/* Checked In Area (Green) */}
          {checkedIn.length > 0 && (
            <path 
              d={`M${PADDING.left},${getYPosition(checkedIn[0])} 
                  ${datePositions.map((pos, i) => `L${pos},${getYPosition(checkedIn[i])}`).join(' ')}
                  L${width - PADDING.right},${getYPosition(checkedIn[checkedIn.length - 1])}
                  L${width - PADDING.right},${PADDING.top + innerHeight}
                  L${PADDING.left},${PADDING.top + innerHeight} Z`}
              fill="url(#checkInGradient)"
              opacity="0.2"
            />
          )}
          
          {/* Trend lines */}
          {registrations.length > 0 && (
            <path 
              d={`M${PADDING.left},${getYPosition(registrations[0])} 
                  ${datePositions.map((pos, i) => `L${pos},${getYPosition(registrations[i])}`).join(' ')}`}
              fill="none" 
              stroke="url(#regLineGradient)" 
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              filter="url(#glow)"
            />
          )}
          
          {checkedIn.length > 0 && (
            <path 
              d={`M${PADDING.left},${getYPosition(checkedIn[0])} 
                  ${datePositions.map((pos, i) => `L${pos},${getYPosition(checkedIn[i])}`).join(' ')}`}
              fill="none" 
              stroke="url(#checkInLineGradient)" 
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              filter="url(#glow)"
            />
          )}
          
          {/* Data points */}
          {dates.map((_, i) => (
            <g key={`point-${i}`}>
              {/* Registration point */}
              {registrations[i] !== undefined && (
                <>
                  <circle
                    cx={datePositions[i]}
                    cy={getYPosition(registrations[i])}
                    r="5"
                    fill="#fff"
                    stroke="#F97316"
                    strokeWidth="2.5"
                    filter="url(#pointGlow)"
                  />
                  <circle
                    cx={datePositions[i]}
                    cy={getYPosition(registrations[i])}
                    r="3"
                    fill="#F97316"
                  />
                </>
              )}
              
              {/* Check-in point */}
              {checkedIn[i] !== undefined && (
                <>
                  <circle
                    cx={datePositions[i]}
                    cy={getYPosition(checkedIn[i])}
                    r="5"
                    fill="#fff"
                    stroke="#10B981"
                    strokeWidth="2.5"
                    filter="url(#pointGlow)"
                  />
                  <circle
                    cx={datePositions[i]}
                    cy={getYPosition(checkedIn[i])}
                    r="3"
                    fill="#10B981"
                  />
                </>
              )}
            </g>
          ))}
          
          {/* Gradients and filters */}
          <defs>
            <linearGradient id="regGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#F97316" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#F97316" stopOpacity="0.05" />
            </linearGradient>
            <linearGradient id="checkInGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#10B981" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#10B981" stopOpacity="0.05" />
            </linearGradient>
            <linearGradient id="regLineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#F97316" />
              <stop offset="100%" stopColor="#FB923C" />
            </linearGradient>
            <linearGradient id="checkInLineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#10B981" />
              <stop offset="100%" stopColor="#34D399" />
            </linearGradient>
            <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="1.5" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <filter id="pointGlow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="2" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          
          {/* X and Y axes */}
          <line 
            x1={PADDING.left} 
            y1={PADDING.top + innerHeight} 
            x2={width - PADDING.right} 
            y2={PADDING.top + innerHeight} 
            stroke="#94a3b8" 
            strokeWidth="1.5" 
          />
          <line 
            x1={PADDING.left} 
            y1={PADDING.top} 
            x2={PADDING.left} 
            y2={PADDING.top + innerHeight} 
            stroke="#94a3b8" 
            strokeWidth="1.5" 
          />
        </svg>
        
        {/* Professional tooltip */}
        {tooltip.visible && tooltip.data && (
          <div 
            ref={tooltipRef}
            className="chart-tooltip"
            style={{ 
              left: `${tooltip.x}px`, 
              top: `${tooltip.y}px`,
              transform: isMobile ? 'translateX(-50%)' : 'none'
            }}
            role="tooltip"
            aria-live="polite"
          >
            <div className="tooltip-header">
              <span className="tooltip-date">{tooltip.data.date}</span>
            </div>
            <div className="tooltip-item">
              <div className="tooltip-dot" style={{ backgroundColor: '#F97316' }} />
              <div className="tooltip-label">Registrations:</div>
              <div className="tooltip-value">{tooltip.data.registrations}</div>
            </div>
            <div className="tooltip-item">
              <div className="tooltip-dot" style={{ backgroundColor: '#10B981' }} />
              <div className="tooltip-label">Checked In:</div>
              <div className="tooltip-value">{tooltip.data.checkedIn}</div>
            </div>
          </div>
        )}
      </div>
      
      {/* Legend */}
      <div className="chart-legend">
        <div className="legend-item">
          <div className="legend-color" style={{ background: 'linear-gradient(to right, #F97316, #FB923C)' }} />
          <span>Registrations</span>
        </div>
        <div className="legend-item">
          <div className="legend-color" style={{ background: 'linear-gradient(to right, #10B981, #34D399)' }} />
          <span>Checked In</span>
        </div>
      </div>
      
      {/* Statistics */}
      <div className="chart-stats">
        <div className="stat-item">
          <span className="stat-label">Registrations:</span>
          <span className="stat-value">{registrations.reduce((a, b) => a + b, 0)}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Checked In:</span>
          <span className="stat-value">{checkedIn.reduce((a, b) => a + b, 0)}</span>
        </div>
        {checkedIn.length > 0 && (
          <>
            <div className="stat-item">
              <span className="stat-label">Avg. Attendance:</span>
              <span className="stat-value">{Math.round(checkedIn.reduce((a, b) => a + b, 0) / checkedIn.length)}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Registration Rate:</span>
              <span className="stat-value">
                {registrations.reduce((a, b) => a + b, 0) > 0 
                  ? Math.round((checkedIn.reduce((a, b) => a + b, 0) / registrations.reduce((a, b) => a + b, 0)) * 100) 
                  : 0}%
              </span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Peak Day:</span>
              <span className="stat-value">{dates[checkedIn.indexOf(Math.max(...checkedIn))] || 'N/A'}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DailyAttendanceChart;